import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, ListMusic, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

export default function SetlistsPage() {
  const { user, t } = useAuth();
  const canWrite = ["admin", "music_leader"].includes(user?.role);
  const navigate = useNavigate();
  const [setlists, setSetlists] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const [delSl, setDelSl] = useState(null);
  const [addPiece, setAddPiece] = useState("");

  const load = useCallback(() => {
    api.get("/setlists").then((r) => setSetlists(r.data)).catch((e) => toast.error(e.friendly));
    api.get("/library/tree").then((r) => setNodes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const nodeMap = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);
  const pieces = useMemo(() => nodes.filter((n) => n.kind === "piece"), [nodes]);
  const sl = setlists.find((s) => s.id === selected);

  const create = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/setlists", { name: newName.trim(), items: [] });
      setNewName("");
      load();
      setSelected(data.id);
    } catch (e) { toast.error(e.friendly); }
  };

  const update = async (items, name) => {
    try {
      await api.put(`/setlists/${sl.id}`, { name: name ?? sl.name, items });
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const move = (i, dir) => {
    const arr = [...sl.items];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    update(arr);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 flex-1" data-testid="setlists-page">
      <div className="lg:w-72 shrink-0 app-card p-4">
        <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><ListMusic className="h-5 w-5 brand" /> Setlists</h2>
        <div className="space-y-1 mb-3" data-testid="setlists-list">
          {setlists.map((s) => (
            <button
              key={s.id}
              onClick={() => { setSelected(s.id); setRenaming(false); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${selected === s.id ? "brand-bg text-white" : "hover:bg-white/5"}`}
              data-testid={`setlist-item-${s.id}`}
            >
              <span className="truncate">{s.name}</span>
              <span className="text-xs opacity-60">{s.items.length}</span>
            </button>
          ))}
          {setlists.length === 0 && <p className="text-xs t-secondary">{t("setl.empty")}</p>}
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t("setl.new_placeholder")} className="h-9 bg-transparent border-app text-sm" data-testid="new-setlist-input" />
            <Button size="sm" className="brand-bg text-white h-9" onClick={create} disabled={!newName.trim()} data-testid="create-setlist-button">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 app-card p-5" data-testid="setlist-detail">
        {!sl ? (
          <p className="t-secondary text-sm py-10 text-center">{t("setl.select_prompt")}</p>
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              {renaming ? (
                <div className="flex gap-2 items-center">
                  <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="h-9 bg-transparent border-app" data-testid="rename-setlist-input" autoFocus />
                  <Button size="sm" className="brand-bg text-white h-9" onClick={async () => { await update(sl.items, renameVal.trim() || sl.name); setRenaming(false); }} data-testid="rename-setlist-confirm">
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <h3 className="font-heading text-xl font-bold" data-testid="setlist-name">{sl.name}</h3>
              )}
              {canWrite && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-app" onClick={() => { setRenameVal(sl.name); setRenaming(true); }} data-testid="rename-setlist-button">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-app text-rose-400" onClick={() => setDelSl(sl)} data-testid="delete-setlist-button">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2 mb-4" data-testid="setlist-items">
              {sl.items.length === 0 && <p className="text-sm t-secondary py-4 text-center">{t("setl.no_pieces")}</p>}
              {sl.items.map((id, i) => (
                <div key={`${id}-${i}`} className="flex items-center gap-3 border border-app rounded-lg px-4 py-2.5" data-testid={`setlist-item-row-${i}`}>
                  <span className="text-xs t-secondary w-5">{i + 1}.</span>
                  <button className="flex-1 text-left text-sm brand hover:underline truncate" onClick={() => navigate(`/bibliotheque?node=${id}`)} data-testid={`setlist-piece-${i}`}>
                    {nodeMap[id]?.name || t("setl.piece_deleted")}
                  </button>
                  {canWrite && (
                    <div className="flex gap-1">
                      <button onClick={() => move(i, -1)} className="t-secondary hover:text-white" data-testid={`setlist-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                      <button onClick={() => move(i, 1)} className="t-secondary hover:text-white" data-testid={`setlist-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
                      <button onClick={() => update(sl.items.filter((_, xi) => xi !== i))} className="t-secondary hover:text-rose-400" data-testid={`setlist-remove-${i}`}><X className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {canWrite && (
              <div className="flex gap-2">
                <Select value={addPiece} onValueChange={setAddPiece}>
                  <SelectTrigger className="bg-transparent border-app" data-testid="add-piece-select"><SelectValue placeholder={t("setl.add_piece_placeholder")} /></SelectTrigger>
                  <SelectContent>
                    {pieces.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  className="brand-bg text-white"
                  disabled={!addPiece}
                  onClick={() => { update([...sl.items, addPiece]); setAddPiece(""); }}
                  data-testid="add-piece-button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!delSl}
        title={t("setl.delete_title")}
        description={t("setl.delete_desc", { name: delSl?.name })}
        onConfirm={async () => {
          await api.delete(`/setlists/${delSl.id}`).catch((e) => toast.error(e.friendly));
          setDelSl(null); setSelected(null);
          load();
        }}
        onClose={() => setDelSl(null)}
        testid="delete-setlist-confirm-dialog"
      />
    </div>
  );
}
